export const NAV_LINKS = [
  { label: 'Why Aiva',    href: '#why' },
  { label: 'Our Bots',    href: '#bots' },
  { label: 'How It Works',href: '#how' },
  { label: 'Reports',     href: '#reports' },
  { label: 'Platforms',   href: '#platforms' },
  { label: 'Pricing',     href: '#pricing' },
  { label: 'Contact',     href: '#contact' },
];

export const STATS = [
  { number: '10+',  label: 'Domain-specific bots' },
  { number: '24/7', label: 'Always-on availability' },
  { number: '15+',  label: 'Platform integrations' },
  { number: '80%',  label: 'Query deflection rate' },
];

export const WHY_POINTS = [
  {
    icon: '🧠',
    title: 'Domain intelligence, not generic answers',
    desc: 'Generic bots fail because they only answer static FAQs. Aiva bots understand business context, retrieve trusted knowledge, connect to live systems, and complete approved actions inside the conversation.',
  },
  {
    icon: '🔗',
    title: 'Live data & RAG-powered responses',
    desc: 'Our bots connect to your databases, APIs, and documents in real time using Retrieval-Augmented Generation (RAG) — so answers are always accurate, up-to-date, and grounded in your actual data.',
  },
  {
    icon: '⚡',
    title: 'Action-capable, not just conversational',
    desc: 'Beyond answering questions, Aiva bots can raise tickets, update records, fetch reports, schedule interviews, send notifications, and complete multi-step workflows — all from within the chat.',
  },
  {
    icon: '🛡️',
    title: 'Enterprise-grade security & control',
    desc: 'Role-based access ensures bots only surface data each user is authorised to see. All interactions are logged, auditable, and compliant with your organisation\'s data governance policies.',
  },
];

export const BOTS = [
  {
    id: 'hr',
    emoji: '👩‍💼',
    name: 'HR Assistant Bot',
    tagline: 'Your always-on HR companion',
    color: 'teal',
    tags: ['Leave & Attendance', 'Salary Slips', 'Policies', 'Onboarding'],
    desc: 'Handles the full spectrum of HR queries so your HR team can focus on strategic work instead of repetitive questions.',
    capabilities: [
      'Check leave balance, apply for leave, and view attendance history',
      'Download salary slips, tax documents, and reimbursement status',
      'Get instant answers on company policies, holidays, and procedures',
      'Onboarding checklists and new-joiner FAQs',
      'Raise HR tickets directly from the conversation',
    ],
    outcomes: ['60% reduction in HR helpdesk tickets', 'Instant answers on 200+ policy questions', '24/7 availability for global teams'],
  },
  {
    id: 'sales',
    emoji: '📈',
    name: 'Sales & Marketing Bot',
    tagline: 'Convert conversations into pipeline',
    color: 'blue',
    tags: ['Lead Capture', 'Product Discovery', 'Demo Scheduling', 'Hot-lead Handoff'],
    desc: 'Engages website visitors and prospects intelligently — qualifying leads, answering product questions, and handing off hot leads to your sales team with full context.',
    capabilities: [
      'Qualify inbound leads with dynamic, branching conversations',
      'Product discovery — surface relevant offerings based on buyer needs',
      'Schedule product demos directly in the chat',
      'Hot-lead handoff to live agents with full conversation history',
      'CRM integration — create and update lead records automatically',
    ],
    outcomes: ['3× faster lead qualification', '40% increase in demo bookings', 'Zero dropped leads with instant handoff'],
  },
  {
    id: 'itsm',
    emoji: '🖥️',
    name: 'IT Helpdesk Bot',
    tagline: 'First-line IT support, automated',
    color: 'purple',
    tags: ['Access Requests', 'Troubleshooting', 'Ticket Raise', 'Escalation'],
    desc: 'Resolves common IT issues instantly and escalates complex ones to the right team with full context — reducing helpdesk load and improving employee experience.',
    capabilities: [
      'Reset passwords and unlock accounts without human intervention',
      'Troubleshoot common software and connectivity issues step by step',
      'Raise ITSM tickets with auto-populated category, priority, and description',
      'Check ticket status and SLA timelines from within the chat',
      'Escalate to Tier 2 agents with complete conversation context',
    ],
    outcomes: ['70% of L1 tickets resolved without human', 'Average resolution time reduced by 65%', 'SLA breach rate cut by 80%'],
  },
  {
    id: 'finance',
    emoji: '💰',
    name: 'Finance & Expense Bot',
    tagline: 'Finance queries, answered instantly',
    color: 'amber',
    tags: ['Expense Claims', 'Budget Queries', 'Invoice Status', 'Approvals'],
    desc: 'Gives employees and managers real-time visibility into financial data, expense status, and approval workflows — without burdening the finance team.',
    capabilities: [
      'Submit and track expense claims with receipt upload',
      'Check budget utilisation by department or project',
      'Query invoice status and payment timelines',
      'Trigger approval workflows for purchases and reimbursements',
      'Pull real-time financial summaries and spend reports',
    ],
    outcomes: ['50% faster expense processing', 'Finance team queries reduced by 55%', 'Full audit trail for every transaction'],
  },
  {
    id: 'onboarding',
    emoji: '🚀',
    name: 'Employee Onboarding Bot',
    tagline: 'Day-one experience, reimagined',
    color: 'green',
    tags: ['Checklists', 'Document Collection', 'Training', 'FAQs'],
    desc: 'Guides new joiners through every step of onboarding — from document submission to first-week training — reducing HR overhead and improving the new-joiner experience.',
    capabilities: [
      'Dynamic onboarding checklists personalised to role and department',
      'Collect and verify documents via secure chat upload',
      'Answer new-joiner FAQs about benefits, policies, and facilities',
      'Assign and track mandatory training completion',
      'Introduce team members and org chart navigation',
    ],
    outcomes: ['Onboarding time reduced from 5 days to 1', '100% document collection before Day 1', 'New-joiner satisfaction up 45%'],
  },
  {
    id: 'vendor',
    emoji: '🤝',
    name: 'Vendor & Procurement Bot',
    tagline: 'Streamline vendor communication',
    color: 'red',
    tags: ['PO Status', 'Vendor Queries', 'Contract FAQs', 'Approval Routing'],
    desc: 'Handles repetitive vendor and procurement queries — PO status, contract questions, invoice tracking — so your procurement team focuses on strategy.',
    capabilities: [
      'Vendors can check PO status, delivery schedules, and payment timelines',
      'Answer contract terms, compliance requirements, and SLA queries',
      'Route procurement approvals to the right stakeholder automatically',
      'Track and notify on invoice processing milestones',
      'Escalate disputes to the procurement team with full context',
    ],
    outcomes: ['80% of vendor queries resolved automatically', 'PO turnaround time reduced by 40%', 'Vendor satisfaction scores up 35%'],
  },
  {
    id: 'support',
    emoji: '🎯',
    name: 'Customer Support Bot',
    tagline: 'Delight customers at scale',
    color: 'teal',
    tags: ['Order Status', 'Returns & Refunds', 'Product FAQs', 'Live Handoff'],
    desc: 'Delivers instant, accurate customer support across every channel — resolving common queries and escalating complex cases to agents with full conversation context.',
    capabilities: [
      'Answer product and service questions with live knowledge base retrieval',
      'Handle order tracking, returns, and refund requests end-to-end',
      'Personalised support with CRM integration and customer history',
      'Sentiment detection — escalate frustrated customers to live agents',
      'Post-resolution CSAT surveys and feedback collection',
    ],
    outcomes: ['85% first-contact resolution rate', 'Support cost per ticket reduced by 60%', 'CSAT score improved by 28 points'],
  },
  {
    id: 'custom',
    emoji: '🔧',
    name: 'Custom Domain Bot',
    tagline: 'Built for your exact workflow',
    color: 'purple',
    tags: ['Any Domain', 'Custom Workflows', 'API Integration', 'Fully Branded'],
    desc: 'Every organisation has unique processes. We design and deploy fully custom AI agents trained on your data, integrated with your systems, and branded to your identity.',
    capabilities: [
      'Domain-specific training on your internal documents and FAQs',
      'Custom conversation flows mapped to your exact business processes',
      'Deep integration with any internal system via REST API or webhook',
      'Fully branded — your logo, tone, and language',
      'Continuous improvement with feedback loops and usage analytics',
    ],
    outcomes: ['Tailored to your exact use case', 'Live in 2–4 weeks', 'Full ownership of data and models'],
  },
];

export const HOW_STEPS = [
  {
    num: '01',
    title: 'Discovery & Domain Mapping',
    desc: 'We start by understanding your business processes, common queries, data sources, and integration requirements. We map every conversation flow before writing a single line of code.',
    tag: '1–2 weeks',
  },
  {
    num: '02',
    title: 'Data Ingestion & RAG Setup',
    desc: 'Your documents, FAQs, policies, and databases are ingested and indexed. We configure Retrieval-Augmented Generation (RAG) so the bot always answers from trusted, current data.',
    tag: '1–2 weeks',
  },
  {
    num: '03',
    title: 'Bot Training & Flow Design',
    desc: 'Conversation flows are designed, tested, and refined. The bot is trained on domain-specific intent recognition, escalation rules, and action triggers.',
    tag: '1–2 weeks',
  },
  {
    num: '04',
    title: 'Integration & Platform Deployment',
    desc: 'The bot is integrated with your existing systems — HRMS, CRM, ITSM, ERP — and deployed on your chosen channels: web, Teams, WhatsApp, Slack, or mobile.',
    tag: '1 week',
  },
  {
    num: '05',
    title: 'Go Live & Continuous Improvement',
    desc: 'Post-launch, we monitor performance, analyse conversation logs, and continuously refine the bot based on real usage data, missed intents, and user feedback.',
    tag: 'Ongoing',
  },
];

export const REPORT_FEATURES = [
  {
    icon: '📊',
    title: 'Conversation Analytics Dashboard',
    desc: 'Real-time dashboard showing total conversations, resolution rate, escalation rate, average handle time, and user satisfaction — by bot, channel, date range, and department.',
    tags: ['Real-time', 'Filterable', 'Exportable'],
  },
  {
    icon: '🔍',
    title: 'Intent & Query Analysis',
    desc: 'Understand exactly what your users are asking. Track top intents, missed queries, fallback rates, and trending topics to continuously improve bot accuracy.',
    tags: ['Top intents', 'Missed queries', 'Trends'],
  },
  {
    icon: '📈',
    title: 'Resolution & Deflection Reports',
    desc: 'Track how many queries were resolved without human intervention. Measure ticket deflection rates by category, team, and time period to quantify ROI.',
    tags: ['Deflection rate', 'ROI metrics', 'By category'],
  },
  {
    icon: '⏱',
    title: 'SLA & Response Time Reports',
    desc: 'Monitor first-response time, resolution time, and SLA compliance across all bot interactions. Identify bottlenecks and optimise escalation rules.',
    tags: ['SLA compliance', 'Response time', 'Escalation'],
  },
  {
    icon: '😊',
    title: 'User Satisfaction (CSAT) Reports',
    desc: 'Automated post-conversation CSAT surveys with trend analysis. Drill into low-scored conversations to find root causes and improve user experience.',
    tags: ['CSAT scores', 'Trend analysis', 'Drill-down'],
  },
  {
    icon: '🤖',
    title: 'Bot Performance Scorecard',
    desc: 'A comprehensive scorecard per bot — accuracy, confidence scores, fallback frequency, top performing flows, and areas needing retraining. Actionable, not just informational.',
    tags: ['Accuracy', 'Confidence score', 'Retraining cues'],
  },
];

export const PLATFORMS = [
  { name: 'Web Widget',       icon: '🌐' },
  { name: 'Microsoft Teams',  icon: '💼' },
  { name: 'WhatsApp',         icon: '📱' },
  { name: 'Slack',            icon: '💬' },
  { name: 'Mobile Apps',      icon: '📲' },
  { name: 'CRM Systems',      icon: '🗂️' },
  { name: 'HRMS',             icon: '👥' },
  { name: 'ERP Systems',      icon: '⚙️' },
  { name: 'ITSM / Helpdesk',  icon: '🎫' },
  { name: 'Email',            icon: '📧' },
  { name: 'SharePoint',       icon: '📁' },
  { name: 'Custom API',       icon: '🔌' },
];

export const TESTIMONIALS = [
  {
    initials: 'RK',
    name: 'Rajesh Kumar',
    title: 'CHRO · Manufacturing Enterprise',
    quote: '"The HR bot handles 600+ employee queries every month without a single ticket reaching our HR team. Leave balance, salary slips, policy questions — all answered instantly, 24/7."',
  },
  {
    initials: 'PS',
    name: 'Priya Sharma',
    title: 'Head of IT · Financial Services',
    quote: '"Our IT helpdesk bot resolved 73% of L1 tickets autonomously in the first month. The escalation handoff is seamless — agents receive full conversation context, so there is zero repetition."',
  },
  {
    initials: 'AM',
    name: 'Arjun Mehta',
    title: 'VP Sales · SaaS Company',
    quote: '"The sales bot qualifies leads at 3am when our team is offline. Demo bookings went up 40% and lead response time dropped from 4 hours to under 2 minutes."',
  },
];

export const FOOTER_LINKS = [
  { label: 'Privacy Policy', href: '#' },
  { label: 'Terms',          href: '#' },
  { label: 'Docs',           href: '#' },
  { label: 'Support',        href: 'mailto:info@aiprus.com' },
  { label: 'aiprus.com',     href: 'https://www.aiprus.com' },
];
